import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../app/store';
import ReactPaginate from 'react-paginate';
import { formatCurrency } from '../utils/utility';
import { setIsNeedToGoFirstTransactionPageFlag } from '../features/walletSlice';
import { getUserTransactions, Transaction } from 'keycloakauthmodule';

interface LocalTransaction {
  date: string;
  detail: string;
  amount: number;
  type: 'Credit' | 'Debit';
  balance: number;
  box_id: string;
  box_title: string;
  api_type: string;
  payment_type: string;
}

// Conversion function
const convertToLocalTransaction = (apiTransaction: Transaction): LocalTransaction => {
  return {
    date: new Date(apiTransaction.created_at + 'Z').toLocaleString(),
    detail: apiTransaction.description,
    box_id: apiTransaction.box_id,
    box_title: apiTransaction.box_title,
    api_type: apiTransaction.type,
    amount: apiTransaction.credit - apiTransaction.debit,
    type: apiTransaction.credit > 0 ? 'Credit' : 'Debit',
    balance: apiTransaction.balance_after,
    payment_type: apiTransaction.payment_type,
  };
};

const Container = styled.div`
  display: inline-block;
  background: transparent;
  margin-top: 1rem;
  width: 100%;
  border: 1px solid #004d4d;
`;

const Head = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #000d0d;
  color: #d9ffff;
  height: 40px;
  padding-left: 1rem;

  span {
    font-weight: bold;
  }

  div {
    display: flex;
    align-items: center;

    span {
      font-weight: normal;
    }
  }
`;



const Select = styled.select`
  margin-left: 0.5rem;
`;

const Option = styled.option``;

const TransactionTable = styled.div`
  width: 100%;
  border: 1px solid #004d4d;
`;

const Row = styled.div`
  display: flex;
  padding: 0.5rem 0;
  border-bottom: 1px solid #004d4d;

  &:last-child {
    border-bottom: none;
  }
`;

const DateCell = styled.div`
  flex-basis: 20%; /* Adjust percentage as needed for Date column */
  text-align: center;
`;

const HeadingDateCell = styled.div`
  color: #ccfffb;
  flex-basis: 20%; /* Adjust percentage as needed for Date column */
  text-align: center;
`;

const HeadingDetailCell = styled.div`
  flex-basis: 50%; /* Adjust percentage as needed for Detail column */
  text-align: center; /* Left-aligning to match the provided example */

  overflow: hidden;
  text-overflow: ellipsis;
  word-wrap: break-word;
  color: #ccfffb;
`;

const DetailCell = styled.div`
  flex-basis: 50%; /* Adjust percentage as needed for Detail column */
  text-align: left; /* Left-aligning to match the provided example */

  overflow: hidden;
  text-overflow: ellipsis;
  word-wrap: break-word;
  color: #ccfffb;
`;


const HeadingAmountCell = styled.div`
  color: #ccfffb;
  flex-basis: 10%; /* Adjust percentage as needed for Date column */
  text-align: center;
`;


interface CellProps {
  type: 'Credit' | 'Debit';
}

const AmountCell = styled.div<CellProps>`
  flex-basis: 10%;
  text-align: center;
  display: flex;
  justify-content: flex-end;
  padding-right: 25px;
  color: ${(props) => (props.type === 'Credit' ? '#28a745' : '#ff4c4c')}; /* Green for Credit, Red for Debit */
`;

const PaymentTypeCell = styled.div`
  flex-basis: 10%; /* Adjust percentage as needed for Detail column */
  text-align: center; 
  overflow: hidden;
  text-overflow: ellipsis;
  word-wrap: break-word;
  color: #ccfffb;
`;

const CreditDebitCell = styled.div<CellProps>`
  flex-basis: 10%;
  text-align: center;
  color: ${(props) => (props.type === 'Credit' ? '#28a745' : '#ff4c4c')}; /* Green for Credit, Red for Debit */
`;


const HeadingCreditDebitCell = styled.div`
  color: #ccfffb;
  flex-basis: 10%; /* Adjust percentage as needed for Date column */
  text-align: center;
`;

const BalanceCell = styled.div`
  flex-basis: 10%; /* Smaller space for Balance */
  text-align: center;
  color: #ccfffb; /* Example color */
`;

const HeadingBalanceCell = styled.div`
  color: #ccfffb;
  flex-basis: 10%; /* Adjust percentage as needed for Date column */
  text-align: center;
`;


const HeadingRow = styled(Row)`
  height: 24px;
  background-color: #004d4d;
  padding: 0 8px;
`;


const HeadBalance = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #004d4d;
  height: 50px;
  padding-left: 1rem;
  border: 1px solid rgb(0, 108, 103); /* Example border color */
`;

const Title = styled.span`
  font-size: 1.5rem;
  font-weight: bold;
  color: white;
`;


const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: 0.3s ease-in-out;
  border: none;
  color: white;
  min-width: 150px;
  text-align: center;
  justify-content: center;

  &:hover {
    opacity: 0.85;
  }

  @media (max-width: 768px) {
    font-size: 13px; /* Reduce font size */
    padding: 8px 14px; /* Smaller padding */
    min-width: 130px;
  }

  @media (max-width: 480px) {
    width: 100%; /* Full width on very small screens */
  }
`;


const Spacer = styled.div`
  height: 100px;
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 20px 0;
`;


const TransactionDetail = ({ transaction }: { transaction: LocalTransaction }) => {
  const baseUrl = window.location.origin;
  const boxLink = `${baseUrl}/boxes/${transaction.box_id}`;

  const detailContent = transaction.api_type.includes('box_opened') ? (
    <>
      Box opened{' '}
      <a href={boxLink} target="_blank" rel="noopener noreferrer">
        {transaction.box_title}
      </a>
    </>
  ) : (
    transaction.detail
  );

  return (
    <DetailCell className={'normal'}>
      {detailContent}
    </DetailCell>
  );
};

const TransactionComponent: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const dispatch: AppDispatch = useDispatch();
  const { isNeedToGoFirstTransactionPage } = useSelector((state: RootState) => state.wallet);
  const transactions = useSelector((state: RootState) => state.authSlice.transactions || []);
  const transactionsLoading = useSelector((state: RootState) => state.authSlice.transactionsLoading);
  const transactionsError = useSelector((state: RootState) => state.authSlice.transactionsError);
  const totalTransactions = useSelector((state: RootState) => state.user.totalTransactions);

  const offset = (currentPage - 1) * limit;

  useEffect(() => {
    dispatch(getUserTransactions({ offset, limit }));
  }, [dispatch, offset, limit]);

  useEffect(() => {
    setCurrentPage(1);
    dispatch(getUserTransactions({ offset, limit }));
    dispatch(setIsNeedToGoFirstTransactionPageFlag(false));
  }, [dispatch, isNeedToGoFirstTransactionPage]);

  const handlePageChange = (selectedPage: number) => {
    setCurrentPage(selectedPage);
  };

  const formattedTransactions: LocalTransaction[] = transactions.map(convertToLocalTransaction);

  return (
    <>
      <Container>
        <HeadBalance>
          <Title>Transactions</Title>
        </HeadBalance>
        <TransactionTable>
          <HeadingRow>
            <HeadingDateCell>Date</HeadingDateCell>
            <HeadingDetailCell>Detail</HeadingDetailCell>
            <HeadingAmountCell>Amount</HeadingAmountCell>
            <HeadingAmountCell>Currency</HeadingAmountCell>
            <HeadingCreditDebitCell>Credit/Debit</HeadingCreditDebitCell>
            <HeadingBalanceCell>Balance</HeadingBalanceCell>
          </HeadingRow>
          {transactionsLoading ? (
            <div>Loading...</div>
          ) : transactionsError ? (
            <div>Error loading transactions</div>
          ) : (
            formattedTransactions.map((transaction, index) => (
              <Row key={index}>
                <DateCell>{transaction.date}</DateCell>
                <TransactionDetail transaction={transaction} />
                <AmountCell type={transaction.type}>
                  {formatCurrency(transaction.amount)}
                </AmountCell>
                <PaymentTypeCell className={'normal'}>
                {transaction.payment_type.toUpperCase() === "USD" ? "USDC" : transaction.payment_type.toUpperCase()}
                </PaymentTypeCell>
                <CreditDebitCell type={transaction.type}>
                  {transaction.type}
                </CreditDebitCell>
                <BalanceCell>{formatCurrency(transaction.balance)}</BalanceCell>
              </Row>
            ))
          )}
        </TransactionTable>

        <PaginationWrapper>
          <ReactPaginate
            pageCount={Math.ceil(totalTransactions / limit)}
            pageRangeDisplayed={5}
            marginPagesDisplayed={2}
            onPageChange={({ selected }) => handlePageChange(selected + 1)}
            previousLabel={'Previous'}
            nextLabel={'Next'}
            breakLabel={'...'}
            containerClassName={'pagination'}
            pageClassName={'page-item'}
            pageLinkClassName={'page-link'}
            previousClassName={'page-item'}
            previousLinkClassName={'page-link'}
            nextClassName={'page-item'}
            nextLinkClassName={'page-link'}
            breakClassName={'page-item'}
            breakLinkClassName={'page-link'}
            activeClassName={'active'}
            forcePage={currentPage - 1} // Set the current page programmatically
          />
        </PaginationWrapper>
      </Container>
      <Spacer />
    </>
  );
};

export default TransactionComponent;
